import { useEffect, useState } from "react";
import { Box, Stack, Button, Typography, Drawer, TextField, IconButton, Divider, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Grid, ListItemText, Checkbox } from "@mui/material";
import { useWindowSize } from "../../../utils/useWindowSize";

import { userState } from "../../../interface/MainInterface";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

import LoadingCircle from "../../../utils/LoadingCircle";
import { HttpAdminApi } from "../../../interface/admin-api";

interface PropsType {
  userState: userState;
  broad_seq: string;
  callback: any;
}

interface FAQItem {
  pk: string;
  sk: string;
  faq_id?: string;
  question: string;
  answer: string;
  is_common?: boolean; // 서버에서 내려주는 공통 여부 플래그
}

interface TemplateOption {
  template_id: string;
  template_name: string;
  description?: string;
}

const scrollBarStyle = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' },
};

const adminApi = new HttpAdminApi();

const FAQManagement = ({ userState, broad_seq, callback }: PropsType) => {
  const windowSize = useWindowSize();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [faqList, setFaqList] = useState<FAQItem[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [allTemplates, setAllTemplates] = useState<TemplateOption[]>([]);

  const [menuOpen, setMenuOpen] = useState(false);

  // ✅ 현재 선택된 템플릿의 상세 정보를 찾기 위한 변수
  // const selectedTemplate = allTemplates.find(t => t.template_id === selectedTemplateId);

  // useEffect(() => {
  //   get_faq_list();
  // }, []);

  useEffect(() => {
    if (open) {
      initData();
    }
  }, [open]);

  const initData = async () => {
    setIsLoading(true);
    await Promise.all([get_template_options(), get_faq_list()]);
    setIsLoading(false);
  };

  // 전체 템플릿 목록 조회
  const get_template_options = async () => {
    try {
      const res = await adminApi.post({ command: "get_faq_template_list" });
      if (res.code === "200") {
        console.log("get_template_options res: ", res);
        setAllTemplates(res.response.template_list || []);
      }
    } catch (e) {
      console.error("템플릿 목록 조회 실패", e);
    }
  };

  const get_faq_list = async () => {
    try {
      const payload = {
        command: "get_faq_list",
        broad_seq,
      };

      const res = await adminApi.post(payload);
      if (res.code === "200") {
        setFaqList([...res.response.faq_list]);
        // ✅ 서버 메타데이터에서 선택된 템플릿 ID 설정
        setSelectedTemplateIds(res.response.template_ids || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // 템플릿 선택 핸들러: 선택 즉시 해당 템플릿의 FAQ를 불러와 리스트에 합침
  const handleTemplateChange = async (templateIds: string[]) => {
    // 1) 즉시 UI에서 선택된 ID들을 반영 (Chip 표시용)
    setSelectedTemplateIds(templateIds);
    setIsLoading(true);

    try {
      // 2) 현재 리스트에서 사용자가 직접 작성한 '방송 전용 FAQ'만 먼저 추출
      // filter를 통해 is_common이 false인 것들만 골라냅니다.
      const specificFaqs = faqList.filter(f => !f.is_common);

      if (templateIds.length === 0) {
        // 선택된 템플릿이 없다면 방송 전용 질문만 노출
        setFaqList(specificFaqs);
      } else {
        // 3) 선택된 모든 템플릿의 상세 질문 리스트를 서버에 요청
        const payload = {
          command: "get_faq_list",
          template_ids: templateIds, // ✅ 단수가 아닌 복수(ids)로 전달
        };

        const res = await adminApi.post(payload);
        
        if (res.code === "200" && res.response.faq_list) {
          // 서버에서 온 템플릿 질문들에 is_common: true 마킹
          const templateFaqs = res.response.faq_list.map((f: any) => ({
            ...f,
            is_common: true,
          }));
          
          // ✅ 최종 리스트: [방송 전용 질문] + [템플릿 질문들] 합쳐서 업데이트
          // 이렇게 하면 우측 미리보기 영역(faqList 기반)에 즉시 나타납니다.
          setFaqList([...specificFaqs, ...templateFaqs]);
        }
      }
    } catch (e) {
      console.error("템플릿 FAQ 로드 실패", e);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ 개별 템플릿 삭제 핸들러
  const handleRemoveTemplate = (idToRemove: string, e: React.MouseEvent) => {
    // 이벤트 버블링 방지 (Select 박스가 열리는 것 막음)
    e.stopPropagation();
    const newIds = selectedTemplateIds.filter(id => id !== idToRemove);
    handleTemplateChange(newIds);
  };

  // ✅ 전체 템플릿 초기화 핸들러
  const handleClearTemplates = () => {
    if (window.confirm("선택된 모든 템플릿을 해제하시겠습니까?")) {
      handleTemplateChange([]);
    }
  };

  const handleAddFAQ = () => {
    setFaqList((prev) => {
      // 1. 현재 리스트에서 방송 전용 질문(is_common이 없는 것)의 마지막 인덱스를 찾음
      const lastSpecificIndex = prev.reduce((lastIdx, item, idx) => {
        return !item.is_common ? idx : lastIdx;
      }, -1);

      // 2. 새 질문 객체 생성
      const newFaq = { 
        pk: "", sk: "", faq_id: "", 
        question: "", answer: "", 
        is_common: false // 방송 전용이므로 false
      };

      // 3. 방송 질문 바로 뒤(공통 템플릿 시작 전)에 삽입
      const newList = [...prev];
      newList.splice(lastSpecificIndex + 1, 0, newFaq);
      
      return newList;
    });
  };

  const handleRemoveFAQ = (index: number) => {
    if (window.confirm("이 질문 항목을 삭제하시겠습니까?")) {
      setFaqList((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleChangeFAQ = (index: number, field: keyof FAQItem, value: string) => {
    setFaqList((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSubmit = async () => {
    // 방송 전용 FAQ(공통 제외)만 필터링하여 유효성 검사
    const specificFaqs = faqList.filter(f => !f.is_common);
    const isValid = specificFaqs.every((item) => item.question.trim() !== "" && item.answer.trim() !== "");

    if (!isValid) {
      alert("추가하신 모든 질문과 답변을 입력해주세요.");
      return;
    }

    if (!window.confirm("설정사항을 저장하시겠습니까?")) return;

    setIsLoading(true);
    try {
      // 1. FAQ 리스트 저장 (방송 전용만 전송하거나 전체 전송 - 서버 로직에 따름)
      const cleanFaqList = specificFaqs.map((item, idx) => ({
        faq_id: item.faq_id,
        question: item.question,
        answer: item.answer,
        sort_order: idx, // 순서 재정렬
        // pk, sk는 서버(Repository)에서 broad_seq 기준으로 다시 생성하므로 
        // 여기서 보내지 않거나 기존 값을 유지해도 서버 로직이 덮어씁니다.
      }));

      const payload = {
        command: "create_broad_faq",
        broad_seq: broad_seq,
        faq_list: cleanFaqList,
        template_ids: selectedTemplateIds, // ✅ 선택된 템플릿 ID 함께 전송
      };

      const res = await adminApi.post(payload);
      if (res.code === "200") {
        alert("성공적으로 저장되었습니다.");
        if (callback) callback();
        setOpen(false);
      } else {
        alert("등록 중 오류가 발생했습니다.");
      }
    } catch (error) {
      alert("등록 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{ cursor: "pointer", minWidth: "70px" }}
        onClick={() => setOpen(true)}
      >
        <Stack
          direction={"column"}
          spacing={0}
          sx={{
            backgroundColor: "#1876d2",
            color: "#ffffff",
            borderRadius: "7px",
            p: 1,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <LiveHelpIcon />
          </Box>
          <Typography sx={{ fontSize: "14px", textAlign: "center" }}>FAQ</Typography>
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { 
            width: windowSize.width > 1200 ? "1100px" : "100%", // 넉넉하게 너비 확장
            display: "flex", flexDirection: "column", overflow: "hidden" 
          },
        }}
      >
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "22px", fontWeight: "800" }}>FAQ 컨텐츠 관리</Typography>
            <Typography variant="caption" color="textSecondary">방송 코드: {broad_seq}</Typography>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          {/* ✅ 메인 컨텐츠 영역: 2컬럼 레이아웃 적용 */}
          <Box sx={{ flex: 1, display: "flex", gap: 3, overflow: "hidden", mb: 3 }}>
            
            {/* --- [좌측] 편집 영역 (Input) --- */}
            <Box sx={{ flex: 1.3, display: "flex", flexDirection: "column", gap: 3, overflowY: "auto", pr: 2, ...scrollBarStyle }}>
              <Typography sx={{ fontWeight: 700, fontSize: "16px", display: "flex", alignItems: "center", gap: 1 }}>
                <AddCircleOutlineIcon fontSize="small" color="primary" /> 방송 전용 질문 작성
              </Typography>
              
              <Stack spacing={2}>
                {faqList.map((faq, index) => {
                  if (faq.is_common) return null;
                  return (
                    <Box 
                      key={`edit-${index}`} 
                      sx={{ 
                        p: 2.5, // 여백을 살짝 늘려 내부 요소들과의 간격 확보
                        border: "1px solid #e2e8f0", 
                        borderRadius: "12px", // 조금 더 부드러운 곡선
                        position: "relative", 
                        bgcolor: "#fff",
                        transition: "border-color 0.2s",
                        "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } // 호버 시 테두리 강조
                      }}
                    >
                      {/* ✅ 삭제 아이콘 최적화: 위치를 더 우상단 끝으로 붙이고 크기 조절 */}
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveFAQ(index)} 
                        sx={{ 
                          position: "absolute", 
                          top: 12, // 위쪽 여백 줄임
                          right: 12, // 오른쪽 여백 줄임
                          color: "#94a3b8", // 평소에는 더 연하게
                          padding: "3px", // 클릭 영역은 유지하되 시각적 크기 축소
                          "&:hover": { 
                            color: "#64748b", // 호버 시 색상을 더 진하게 변경 (빨간색 대신)
                            backgroundColor: "#f1f5f9" // 호버 시 연한 회색 배경
                          } 
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "18px" }} /> {/* 아이콘 자체 크기 고정 */}
                      </IconButton>
                      
                      <Stack spacing={2}>
                        {/* ✅ 삭제 버튼과 겹치지 않도록 오른쪽 여백 추가 */}
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: "11px", color: "#1876d2", mb: 0.5, ml: 0.5 }}>
                            QUESTION {index + 1}
                          </Typography>
                          <TextField 
                            placeholder="시청자가 궁금해할 질문을 입력하세요" 
                            fullWidth 
                            variant="outlined"
                            size="small"
                            value={faq.question} 
                            onChange={(e) => handleChangeFAQ(index, "question", e.target.value)}
                            sx={{ 
                              '& .MuiOutlinedInput-root': { 
                                borderRadius: '8px',
                                backgroundColor: '#fcfcfc',
                                '& fieldset': { borderColor: '#e2e8f0' },
                              },
                              '& .MuiInputBase-input': { 
                                fontSize: '16px', // 16px은 다소 커서 14px로 조정
                                py: '8px' 
                              }
                            }}
                          />
                        </Box>
                      
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: "11px", color: "#64748b", mb: 0.5, ml: 0.5 }}>
                            ANSWER
                          </Typography>
                          <TextField 
                            placeholder="답변 내용을 상세히 입력하세요" 
                            fullWidth 
                            multiline 
                            // minRows={2}
                            size="small"
                            value={faq.answer} 
                            onChange={(e) => handleChangeFAQ(index, "answer", e.target.value)} 
                            sx={{ 
                              '& .MuiOutlinedInput-root': { 
                                borderRadius: '8px',
                                backgroundColor: '#fcfcfc',
                                '& fieldset': { borderColor: '#e2e8f0' },
                              },
                              '& .MuiInputBase-input': { 
                                fontSize: '16px',
                                lineHeight: '1.5'
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
                
                <Button 
                  variant="outlined" 
                  startIcon={<AddCircleOutlineIcon />} 
                  onClick={handleAddFAQ} 
                  sx={{ py: 2, borderStyle: "dashed", borderRadius: "16px", color: "#64748b", borderColor: "#cbd5e1", fontSize: '15px', fontWeight: 700 }}
                >
                  질문 항목 추가하기
                </Button>
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* --- [우측] 미리보기 영역 (Preview) --- */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#f8fafc", borderRadius: "8px", p: 2.5, overflowY: "auto", border: "1px solid #f1f5f9", ...scrollBarStyle }}>
              <Typography sx={{ fontWeight: 800, fontSize: "16px", mb: 2, color: "#1e293b", display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" color="primary" /> 실시간 미리보기 (총 {faqList.length}건)
              </Typography>
              
              <Stack spacing={2}>
                {faqList.map((faq, idx) => (
                  <Box 
                    key={`prev-${idx}`} 
                    sx={{ 
                      p: 1.6, 
                      bgcolor: "#fff", 
                      borderRadius: "4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      borderLeft: faq.is_common ? "6px solid #3b82f6" : "6px solid #94a3b8",
                      opacity: faq.is_common ? 0.9 : 1 // 템플릿 질문은 약간의 투명도로 구분 가능
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "14px",
                        color: "#1e293b",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      Q. {faq.question || (faq.is_common ? "" : "내용을 입력해주세요")}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: "13px",
                        color: "#475569",
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      A. {faq.answer || (faq.is_common ? "" : "내용을 입력해주세요")}
                    </Typography>
                    {faq.is_common && (
                      <Chip 
                        label="공통 가이드" 
                        size="small" 
                        sx={{ mt: 1.5, height: "20px", fontSize: "10px", bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }} 
                      />
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* --- [하단] 고정 설정 영역 --- */}
          <Box sx={{ pt: 3, borderTop: "1px solid #f1f5f9" }}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={8}>
                <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>
                      가이드 템플릿 선택
                    </Typography>
                    {selectedTemplateIds.length > 0 && (
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={handleClearTemplates}
                        sx={{ fontSize: "11px", fontWeight: 700 }}
                      >
                        전체 해제
                      </Button>
                    )}
                  </Stack>

                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      open={menuOpen} // ✅ 상태 제어
                      onOpen={() => setMenuOpen(true)}
                      onClose={() => setMenuOpen(false)}
                      value={selectedTemplateIds}
                      onChange={(e) => {
                        handleTemplateChange(e.target.value as string[]);
                        setMenuOpen(false); // ✅ 선택 시 즉시 닫기
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                          {selected.map((id) => (
                            <Chip 
                              key={id} 
                              label={allTemplates.find(t => t.template_id === id)?.template_name} 
                              size="small" 
                              color="primary" 
                              onDelete={(e) => handleRemoveTemplate(id, e)}
                              onMouseDown={(e) => e.stopPropagation()}
                              sx={{ fontWeight: 700, borderRadius: '24px' }}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {allTemplates.map((t) => (
                        <MenuItem key={t.template_id} value={t.template_id} sx={{ py: 1.5, fontWeight: 600 }}>
                          {t.template_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  variant="contained" fullWidth onClick={handleSubmit} disabled={isLoading}
                  sx={{ height: "100%", fontSize: "18px", fontWeight: 900, borderRadius: "8px", background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)", boxShadow: '0 8px 20px rgba(30, 64, 175, 0.2)' }}
                >
                  {isLoading ? "저장 중..." : "설정 저장"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default FAQManagement;
